import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { apiAuth, apiClient, clearToken, getToken, saveToken } from "../utils/http";

const AuthContext = createContext(null);

const pickSession = (data) => ({ user: data.user, store: data.store, config: data.config });

/*
 * Only the token is persisted; user, store and module config come from
 * GET init on every load, so a store that has the module switched off (or a
 * user who lost the Job Order role) is caught before anything renders.
 */
export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [status, setStatus] = useState(() => (getToken() ? "loading" : "anonymous"));
  const [error, setError] = useState("");

  const loadSession = useCallback(() => {
    setStatus("loading");
    apiClient
      .get("init")
      .then((res) => {
        setSession(pickSession(res.data));
        setStatus("ready");
      })
      .catch((err) => {
        // 401 / module-access 403 already redirect to /login in the http interceptor.
        if (err.status === 401 || err.status === 403) return;
        setError(err.message);
        setStatus("error");
      });
  }, []);

  useEffect(() => {
    if (getToken()) loadSession();
  }, [loadSession]);

  const begin = useCallback((data, remember) => {
    saveToken(data.token, remember);
    setSession(pickSession(data));
    setStatus("ready");
  }, []);

  const login = useCallback(
    async (username, password, remember) => {
      const res = await apiAuth.post("login", { username, password });
      begin(res.data, remember);
    },
    [begin]
  );

  const loginWithGoogle = useCallback(
    async (idToken, remember) => {
      const res = await apiAuth.post("login/google", { id_token: idToken });
      begin(res.data, remember);
    },
    [begin]
  );

  /* denzo-admin's "Job Orders" link hands over its own token; swap it for one of our own. */
  const loginWithAdminToken = useCallback(
    async (adminToken) => {
      const res = await apiAuth.post("sso", {}, { headers: { Authorization: `Bearer ${adminToken}` } });
      begin(res.data, true);
    },
    [begin]
  );

  const logout = useCallback(async () => {
    try {
      await apiClient.post("logout");
    } catch (e) {
      // the token is dropped locally either way
    }
    clearToken();
    setSession(null);
    setStatus("anonymous");
  }, []);

  /* e.g. the store admin switching assignment on/off; no need to reload the whole session. */
  const updateConfig = useCallback(
    (patch) => setSession((current) => (current ? { ...current, config: { ...current.config, ...patch } } : current)),
    []
  );

  const can = useCallback(
    (permission) => !!session && session.user.permissions.includes(permission),
    [session]
  );

  const value = useMemo(
    () => ({
      ...(session || {}),
      status,
      error,
      isAuthenticated: status === "ready",
      login,
      loginWithGoogle,
      loginWithAdminToken,
      logout,
      retry: loadSession,
      can,
      updateConfig,
    }),
    [session, status, error, login, loginWithGoogle, loginWithAdminToken, logout, loadSession, can, updateConfig]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
